import { Link } from 'react-router';
import { FileText } from 'lucide-react';
import { formatPercent, formatPricePerKWh, type ChargeMode } from '@ste/shared';
import { useLive, useLiveStore } from '../../api/live.ts';
import { useEnergyHistory, useProtocol } from '../../api/queries.ts';
import { EnergyFlow } from '../../components/EnergyFlow.tsx';
import { Banner, Card, Spinner, Stat } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { localDate } from '../../lib/dates.ts';
import { HemsBanner } from '../grid/HemsBanner.tsx';

export function DashboardPage() {
  const live = useLive();
  const status = useLiveStore((s) => s.status);
  const today = useEnergyHistory('day', localDate(new Date())).data;
  const protocol = useProtocol();

  if (!live) {
    return status === 'closed' ? (
      <Banner tone="muted" title={t('common.reconnecting')} />
    ) : (
      <Spinner label={t('common.loading')} />
    );
  }

  return (
    <div className="space-y-4">
      {!live.online && <Banner tone="warm" title={t('common.offline')} />}
      <HemsBanner live={live} />

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card title={t('dashboard.flow')} action={<OwnShare share={live.greenShareHome} />}>
          <EnergyFlow live={live} />
        </Card>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
          <Card>
            <div className="flex items-end justify-between gap-2">
              <Stat
                label={t('dashboard.autarky')}
                value={today?.autarky !== undefined ? formatPercent(today.autarky * 100) : '–'}
                tone="accent"
              />
              <Ring value={today?.autarky} />
            </div>
          </Card>
          <Card>
            <Stat
              label={t('dashboard.selfConsumption')}
              value={today?.selfConsumption !== undefined ? formatPercent(today.selfConsumption * 100) : '–'}
            />
          </Card>
          <Card>
            <div className="flex items-end justify-between gap-2">
              <Stat
                label={t('dashboard.ownShare')}
                value={live.greenShareHome !== undefined ? formatPercent(live.greenShareHome * 100) : '–'}
                tone="accent"
              />
              <Ring value={live.greenShareHome} />
            </div>
          </Card>
          <Card>
            <Stat
              label={t('dashboard.tariff')}
              value={live.tariff.grid !== undefined ? formatPricePerKWh(live.tariff.grid) : '–'}
            />
          </Card>
        </div>
      </div>

      {live.loadpoints.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {live.loadpoints.map((lp) => (
            <Link
              key={lp.id}
              to={lp.kind === 'heatpump' ? '/waermepumpe' : '/laden'}
              className="flex min-h-16 items-center justify-between rounded-3xl bg-surface p-5 shadow-raised transition active:shadow-inset"
            >
              <div>
                <div className="font-medium">{lp.title}</div>
                <div className="text-sm text-muted">
                  {lp.mode ? modeLabel(lp.mode, lp.kind === 'heatpump') : '–'}
                  {lp.dimmed && ' · §14a'}
                </div>
              </div>
              <div className="text-right text-sm text-muted">
                {lp.kind === 'wallbox' && lp.vehicle?.soc !== undefined && formatPercent(lp.vehicle.soc)}
                {lp.kind === 'heatpump' && lp.temperature !== undefined && `${Math.round(lp.temperature)} °C`}
              </div>
            </Link>
          ))}
        </div>
      )}

      {protocol.isSuccess && (
        <Link
          to="/protokoll"
          className="flex min-h-11 items-center gap-2 text-sm text-accent-text underline lg:hidden"
        >
          <FileText size={16} aria-hidden /> {t('protocol.open')}
        </Link>
      )}
    </div>
  );
}

/** share of own power right now, as a pill next to the card title */
function OwnShare({ share }: { share: number | undefined }) {
  if (share === undefined) return null;
  return (
    <span className="rounded-full bg-accent-soft px-2.5 py-1 text-xs font-semibold text-accent-text">
      {t('dashboard.ownSharePill', { value: formatPercent(share * 100) })}
    </span>
  );
}

/** small progress ring for a share between 0 and 1 */
function Ring({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  const radius = 20;
  const length = 2 * Math.PI * radius;
  const share = Math.min(1, Math.max(0, value));
  return (
    <svg viewBox="0 0 48 48" className="hidden h-12 w-12 shrink-0 min-[400px]:block" aria-hidden>
      <circle cx="24" cy="24" r={radius} fill="none" stroke="var(--surface-2)" strokeWidth="7" />
      <circle
        cx="24"
        cy="24"
        r={radius}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${share * length} ${length}`}
        transform="rotate(-90 24 24)"
      />
    </svg>
  );
}

export function modeLabel(mode: ChargeMode, heatpump: boolean): string {
  if (heatpump && mode === 'pv') return t('mode.heatpump.pv');
  return t(`mode.${mode}`);
}
